import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../LanguageContext";
import { classifyContact } from "../lib/contact";
import { generateUuid } from "../lib/uuid";

const INITIAL_LEAD = { name: "", contact: "", city: "", consent: false };

function getResultProfile(answers) {
  if (answers.motivation === "content" || answers.outcome === "content") return "create";
  if (answers.motivation === "career" || answers.outcome === "career") return "career";
  if (
    answers.outcome === "confidence" ||
    ["crash", "control", "rules", "doubt"].includes(answers.barrier)
  ) {
    return "confidence";
  }
  return "explore";
}

export const OpportunityQuiz = () => {
  const { t } = useLang();
  const q = t.quiz;
  const [stage, setStage] = useState("intro");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState(INITIAL_LEAD);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [submissionId, setSubmissionId] = useState(() => generateUuid());
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const resetTurnstile = () => {
    setTurnstileToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {}
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (!siteKey || stage !== "result") return undefined;

    const scriptId = "cf-turnstile-script";
    const renderWidget = () => {
      if (!isMounted || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "dron_lead_submit",
          theme: "dark",
          callback: (token) => {
            if (isMounted) setTurnstileToken(token);
          },
          "expired-callback": () => {
            if (isMounted) setTurnstileToken(null);
          },
          "error-callback": () => {
            if (isMounted) setTurnstileToken(null);
          },
        });
      } catch (error) {
        console.error("Turnstile quiz render error", error);
      }
    };

    const handleScriptLoad = () => renderWidget();
    let loadedScript = null;

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      loadedScript = script;
      script.addEventListener("load", handleScriptLoad);
    }

    return () => {
      isMounted = false;
      if (loadedScript) loadedScript.removeEventListener("load", handleScriptLoad);
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, stage]);

  const resetQuiz = () => {
    setStage("intro");
    setCurrentQuestion(0);
    setAnswers({});
    setLead(INITIAL_LEAD);
    setErrors({});
    setLoading(false);
    resetTurnstile();
    setSubmissionId(generateUuid());
  };

  const startQuiz = () => {
    setStage("questions");
    setCurrentQuestion(0);
    setAnswers({});
    setErrors({});
  };

  const selectAnswer = (optionId) => {
    const question = q.questions[currentQuestion];
    setAnswers((previous) => ({ ...previous, [question.id]: optionId }));
  };

  const goNext = () => {
    const question = q.questions[currentQuestion];
    if (!answers[question.id]) return;
    if (currentQuestion === q.questions.length - 1) {
      setStage("result");
      return;
    }
    setCurrentQuestion((previous) => previous + 1);
  };

  const goBack = () => {
    if (currentQuestion === 0) {
      setStage("intro");
      return;
    }
    setCurrentQuestion((previous) => previous - 1);
  };

  const updateLead = (key, value) => {
    setLead((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: "", form: "" }));
  };

  const validateLead = () => {
    const nextErrors = {};
    if (!lead.name.trim() || lead.name.trim().length < 2) nextErrors.name = q.form.validation.name;
    if (!lead.contact.trim()) nextErrors.contact = q.form.validation.contact;
    if (!lead.city.trim() || lead.city.trim().length < 2) nextErrors.city = q.form.validation.city;
    if (!lead.consent) nextErrors.consent = q.form.validation.consent;
    if (siteKey && !turnstileToken) nextErrors.turnstile = q.form.validation.turnstile;
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitLead = async (event) => {
    event.preventDefault();
    if (!validateLead()) return;

    const { phone, email, telegram } = classifyContact(lead.contact);
    setLoading(true);
    setErrors({});

    try {
      await axios.post("/api/leads", {
        name: lead.name.trim(),
        phone,
        email,
        telegram,
        whatsapp: "",
        city: lead.city.trim(),
        consent: true,
        preferred_date: "",
        // bramka nie przyjmuje osobnego pola na wynik quizu, więc profil jedzie w preferred_time;
        // węzeł 29 workflowu rozpoznaje prefiks „quiz:” i nie podpisuje go jako termin
        preferred_time: `quiz: ${getResultProfile(answers)}`,
        turnstileToken: turnstileToken || "",
        honeypot: "",
        submission_id: submissionId,
      });
      setStage("submitted");
      toast.success(q.form.successTitle);
    } catch (error) {
      resetTurnstile();
      setErrors({ form: q.form.error });
      toast.error(q.form.error);
    } finally {
      setLoading(false);
    }
  };

  const profile = q.result.profiles[getResultProfile(answers)] || q.result.profiles.explore;
  const question = q.questions[currentQuestion];

  return (
    <section id="quiz" className="bg-[#0F0F0F] py-20 sm:py-28 border-y border-white/10" aria-labelledby="quiz-title">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
          <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-[#E02020] mb-4">
            {q.overline}
          </p>
          <h2 id="quiz-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            {stage === "result" || stage === "submitted" ? profile.title : q.title}
          </h2>
          <p className="mt-5 text-[#A3A3A3] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            {stage === "result" || stage === "submitted" ? q.result.intro : q.subtitle}
          </p>
        </div>

        {stage === "intro" && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto text-center">
            <div className="border border-white/10 bg-[#1A1A1A] p-8 sm:p-12">
              <Sparkles className="text-[#E02020] mx-auto mb-5" size={38} strokeWidth={1.6} />
              <p className="text-white/80 leading-relaxed mb-8">{q.subtitle}</p>
              <button
                type="button"
                data-testid="quiz-start"
                onClick={startQuiz}
                className="inline-flex items-center justify-center gap-2 bg-[#E02020] hover:bg-[#B91818] text-white font-medium text-base px-8 py-4 rounded-sm transition-colors"
              >
                {q.start} <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {stage === "questions" && (
          <motion.div key={question.id} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-white/45 mb-4">
              <span>{q.progress}</span>
              <span>{String(currentQuestion + 1).padStart(2, "0")} / {String(q.questions.length).padStart(2, "0")}</span>
            </div>
            <div className="h-1 bg-white/10 mb-8">
              <div className="h-full bg-[#E02020] transition-all" style={{ width: `${((currentQuestion + 1) / q.questions.length) * 100}%` }} />
            </div>

            <fieldset className="border border-white/10 bg-[#1A1A1A] p-6 sm:p-8">
              <legend className="font-display text-2xl sm:text-3xl font-semibold text-white px-1 mb-6">{question.title}</legend>
              <div className="space-y-3">
                {question.options.map((option) => {
                  const selected = answers[question.id] === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      data-testid={`quiz-option-${question.id}-${option.id}`}
                      onClick={() => selectAnswer(option.id)}
                      className={`w-full flex items-center gap-3 text-left p-4 border rounded-sm transition-colors ${selected ? "border-[#E02020] bg-[#E02020]/10 text-white" : "border-white/15 text-white/75 hover:border-white/40 hover:text-white"}`}
                    >
                      <span className={`grid place-items-center w-5 h-5 border shrink-0 ${selected ? "border-[#E02020] bg-[#E02020]" : "border-white/30"}`}>
                        {selected && <Check size={14} />}
                      </span>
                      <span>{option.text}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-white/10">
                <button type="button" data-testid="quiz-back" onClick={goBack} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
                  <ArrowLeft size={16} /> {q.back}
                </button>
                <button
                  type="button"
                  data-testid="quiz-next"
                  onClick={goNext}
                  disabled={!answers[question.id]}
                  className="inline-flex items-center gap-2 bg-[#E02020] hover:bg-[#B91818] disabled:opacity-40 text-white font-medium px-5 py-3 rounded-sm transition-colors"
                >
                  {q.next} <ArrowRight size={17} />
                </button>
              </div>
            </fieldset>
          </motion.div>
        )}

        {stage === "result" && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 items-start">
            <div className="border border-[#E02020]/40 bg-[#1A1A1A] p-6 sm:p-8">
              <p className="text-white/75 leading-relaxed mb-6">{profile.text}</p>
              <ul className="space-y-3 mb-7">
                {q.result.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3 text-sm text-white/85">
                    <Check className="text-[#E02020] shrink-0 mt-0.5" size={18} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-white/10 pt-5 font-display font-semibold text-white">{q.result.firstMeeting}</div>
            </div>

            <form onSubmit={submitLead} className="bg-[#1A1A1A] border border-white/10 p-6 sm:p-8 space-y-4" data-testid="quiz-lead-form">
              <h3 className="font-display text-2xl font-semibold text-white mb-5">{q.form.title}</h3>
              <div>
                <label htmlFor="quiz-name" className="block text-sm text-white/80 mb-1.5">{q.form.name} *</label>
                <input id="quiz-name" data-testid="quiz-input-name" value={lead.name} onChange={(event) => updateLead("name", event.target.value)} className="w-full bg-black border border-white/20 focus:border-[#E02020] focus:outline-none text-white placeholder-white/30 px-4 py-3 rounded-sm transition-colors" placeholder={q.form.placeholderName} autoComplete="name" />
                {errors.name && <p className="text-[#E02020] text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="quiz-contact" className="block text-sm text-white/80 mb-1.5">{q.form.contact} *</label>
                <input id="quiz-contact" data-testid="quiz-input-contact" value={lead.contact} onChange={(event) => updateLead("contact", event.target.value)} className="w-full bg-black border border-white/20 focus:border-[#E02020] focus:outline-none text-white placeholder-white/30 px-4 py-3 rounded-sm transition-colors" placeholder={q.form.placeholderContact} autoComplete="tel" />
                {errors.contact && <p className="text-[#E02020] text-xs mt-1">{errors.contact}</p>}
              </div>
              <div>
                <label htmlFor="quiz-city" className="block text-sm text-white/80 mb-1.5">{q.form.city} *</label>
                <input id="quiz-city" data-testid="quiz-input-city" value={lead.city} onChange={(event) => updateLead("city", event.target.value)} className="w-full bg-black border border-white/20 focus:border-[#E02020] focus:outline-none text-white placeholder-white/30 px-4 py-3 rounded-sm transition-colors" placeholder={q.form.placeholderCity} autoComplete="address-level2" />
                {errors.city && <p className="text-[#E02020] text-xs mt-1">{errors.city}</p>}
              </div>

              <label className="flex items-start gap-3 text-sm text-white/70 pt-1 cursor-pointer">
                <input type="checkbox" data-testid="quiz-input-consent" checked={lead.consent} onChange={(event) => updateLead("consent", event.target.checked)} className="mt-1 w-4 h-4 accent-[#E02020]" />
                <span>
                  {q.form.consentPre} <Link to="/polityka-prywatnosci" data-testid="quiz-privacy-link" className="text-white underline underline-offset-2 hover:text-[#E02020]">{q.form.consentLink}</Link>.
                </span>
              </label>
              {errors.consent && <p className="text-[#E02020] text-xs">{errors.consent}</p>}

              {siteKey && <div className="flex justify-center pt-2"><div ref={containerRef} id="cf-turnstile-quiz-container" data-testid="quiz-turnstile-container" /></div>}
              {errors.turnstile && <p className="text-[#E02020] text-xs text-center">{errors.turnstile}</p>}
              {errors.form && <p className="text-[#E02020] text-sm">{errors.form}</p>}

              <button type="submit" data-testid="quiz-submit" disabled={loading || (Boolean(siteKey) && !turnstileToken)} className="w-full inline-flex items-center justify-center gap-2 bg-[#E02020] hover:bg-[#B91818] disabled:opacity-60 text-white font-medium text-base px-8 py-4 rounded-sm transition-colors">
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? q.form.sending : q.form.submit}
              </button>
              <button type="button" data-testid="quiz-restart" onClick={resetQuiz} className="w-full inline-flex items-center justify-center gap-2 text-sm text-white/55 hover:text-white transition-colors pt-1">
                <RotateCcw size={15} /> {q.restart}
              </button>
            </form>
          </motion.div>
        )}

        {stage === "submitted" && (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="max-w-xl mx-auto text-center border border-[#E02020]/40 bg-[#1A1A1A] p-8 sm:p-12">
            <Check className="text-[#E02020] mx-auto mb-5" size={42} />
            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-3">{q.form.successTitle}</h3>
            <p className="text-[#A3A3A3] leading-relaxed mb-8">{q.form.successText}</p>
            <button type="button" data-testid="quiz-success-reset" onClick={resetQuiz} className="inline-flex items-center gap-2 border border-white/20 text-white hover:bg-white/10 px-6 py-3 rounded-sm transition-colors">
              <RotateCcw size={16} /> {q.restart}
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};
